import React, { memo, useCallback, useMemo } from 'react';

import { cx } from '@compiled/react';
import { defineMessages, useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import InlineEdit from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Text } from '@atlaskit/primitives/compiled';
import Select, { type ValueType } from '@atlaskit/select';
import { layers } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import ListWithPopup from '@atlassian/list-with-popup/ListWithPopup.tsx';
import { TagItem, TagItemPopupContentWrapper } from '@atlassian/list-with-popup/TagItem.tsx';

import type { FieldBaseProps } from '../common/types';
import { useValidateRequiredField } from '../hooks/useValidateRequiredField';

import type { JiraComponentsField$key } from './__generated__/JiraComponentsField.graphql';
import { ValidationPopup } from './ValidationPopup';

const messages = defineMessages({
	showMoreTooltip: {
		id: 'jira.platform-fields.components.show-more',
		defaultMessage: 'Show more',
		description: 'Tooltip for the show more items button',
	},
	none: {
		id: 'jira.jira-platform-fields.components.none',
		defaultMessage: 'None',
		description: 'Value when no components are selected',
	},
	isRequiredError: {
		id: 'jira.jira-platform-fields.components.is-required-error',
		defaultMessage: '{fieldName} is required',
		description: 'Error message for required fields',
	},
});

const styles = cssMap({
	container: {
		width: '100%',
		// The InlineEdit component has a default 8px margin top, so we are negating this here
		marginTop: token('space.negative.100'),
	},
	readView: {
		width: '100%',
		height: '40px',
		display: 'flex',
		alignItems: 'center',
		paddingRight: token('space.100'),
		paddingLeft: token('space.100'),
	},
	editableReadView: {
		cursor: 'pointer',
		height: '36px', // 40px - 2 * 2px border
	},
});

type Option = { label: string; value: string };

export type Props = FieldBaseProps & {
	field: JiraComponentsField$key;
	onChange?: (newValue: string[] | null) => void;
};

const ReadView = memo(({ options, isEditable }: { isEditable: boolean; options: Option[] }) => {
	const { formatMessage } = useIntl();

	return (
		<Box xcss={cx(styles.readView, isEditable && styles.editableReadView)}>
			{options.length === 0 ? (
				<Text align="start" color="color.text.subtle" maxLines={1}>
					{formatMessage(messages.none)}
				</Text>
			) : (
				<ListWithPopup
					items={options.map((opt) => ({
						key: opt.value,
						name: opt.label,
						isColored: false,
					}))}
					ItemComponent={TagItem}
					PopupContentWrapper={TagItemPopupContentWrapper}
					showMoreTooltip={formatMessage(messages.showMoreTooltip)}
					isHoverPopoverEnabled
				/>
			)}
		</Box>
	);
});

export const JiraComponentsField: React.MemoExoticComponent<
	({ field, fieldValue, isFieldEditable, onChange }: Props) => JSX.Element | null
> = memo(({ field, fieldValue, isFieldEditable = true, onChange }: Props): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const data = useFragment<JiraComponentsField$key>(
		graphql`
			fragment JiraComponentsField on JiraComponentsField {
				name
				fieldConfig {
					isRequired
					isEditable
				}
				components {
					edges {
						node {
							componentId
							name
						}
					}
				}
			}
		`,
		field,
	);

	const fieldName = data?.name;
	const isEditable = !!data?.fieldConfig?.isEditable && isFieldEditable;
	const isRequired = !!data?.fieldConfig?.isRequired;

	const validate = useValidateRequiredField({
		isRequired,
		isEditable,
		fieldName,
		fieldTypeForAnalytics: 'ComponentsField',
	});

	const allowedOptions: Option[] = useMemo(() => {
		const edges = data?.components?.edges;
		return (
			edges
				?.filter(Boolean)
				.map((edge) => {
					const node = edge?.node;
					const value = String(node?.componentId ?? '');
					const label = node?.name ?? value;
					return { label, value };
				})
				.filter((o) => o.value) ?? []
		);
	}, [data?.components?.edges]);

	const selectedOptions: Option[] = useMemo(() => {
		if (Array.isArray(fieldValue) && fieldValue.length > 0) {
			return allowedOptions.filter(
				(o) => fieldValue.includes(o.value) || fieldValue.includes(o.label),
			);
		}
		return [];
	}, [fieldValue, allowedOptions]);

	const handleOnConfirm = useCallback(
		(newValues: ValueType<Option, true>) => {
			onChange?.(newValues.length > 0 ? newValues.map((opt) => opt.value) : null);
		},
		[onChange],
	);

	const readView = useMemo(
		() => <ReadView options={selectedOptions} isEditable={isEditable} />,
		[selectedOptions, isEditable],
	);

	if (!isEditable) {
		return readView;
	}

	return (
		<Box xcss={styles.container}>
			<InlineEdit<ValueType<Option, true>>
				defaultValue={selectedOptions}
				readView={() => readView}
				editView={({ errorMessage, isInvalid, ...fieldProps }) => (
					<ValidationPopup
						errorMessage={errorMessage}
						showInvalidPopup={Boolean(isInvalid && errorMessage)}
						trigger={({ ref }) => (
							<Box ref={ref}>
								<Select<Option, true>
									{...fieldProps}
									menuPortalTarget={
										fg('gravityai-4581-jira-aifc-iframe-korosu') ? undefined : document.body
									}
									menuPosition={fg('gravityai-4581-jira-aifc-iframe-korosu') ? 'fixed' : 'absolute'}
									menuPlacement="auto"
									styles={
										fg('gravityai-4581-jira-aifc-iframe-korosu')
											? {
													menu: (base) => ({ ...base, zIndex: layers.modal() }),
													menuPortal: (base) => ({ ...base, zIndex: layers.modal() }),
												}
											: undefined
									}
									label={fieldName}
									isMulti
									autoFocus
									options={allowedOptions}
									getOptionValue={(o) => o.value}
									getOptionLabel={(o) => o.label}
									placeholder={formatMessage(messages.showMoreTooltip)}
									isInvalid={isInvalid}
								/>
							</Box>
						)}
					/>
				)}
				validate={validate}
				onConfirm={handleOnConfirm}
				hideActionButtons
				readViewFitContainerWidth
				editButtonLabel={selectedOptions.map((o) => o.label).join(', ') ?? undefined}
			/>
		</Box>
	);
});
